/**
 * Ylse Image Slider
 * ─────────────────
 * • Swipe / touch drag
 * • Mouse drag (desktop)
 * • Autoplay (pauses on hover / focus)
 * • Dot indicators
 * • Keyboard accessible (←/→ when focused)
 * • Respects prefers-reduced-motion
 *
 * Add this script once, at the bottom of your theme.liquid
 * (or load it as a deferred asset).
 */

(function () {
  'use strict';

  const SWIPE_THRESHOLD   = 40;   // px needed to register a swipe
  const DEFAULT_SPEED     = 4000; // ms between auto-advances

  function initSlider(el) {
    const track     = el.querySelector('.ylse-slider__track');
    const slides    = el.querySelectorAll('.ylse-slider__slide');
    const dots      = el.querySelectorAll('.ylse-slider__dot');
    const total     = slides.length;
    const speed     = parseInt(el.dataset.autoplaySpeed, 10) || DEFAULT_SPEED;
    const autoplay  = el.dataset.autoplay === 'true';
    const reduced   = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (total < 2) return; // nothing to slide

    let current   = 0;
    let timer     = null;
    let dragStartX = null;
    let dragging  = false;
    let dragDelta = 0;

    /* ── Go to slide ── */
    function goTo(index) {
      current = (index + total) % total;
      track.style.transform = `translateX(-${current * 100}%)`;

      slides.forEach((s, i) => {
        s.setAttribute('aria-hidden', i !== current);
      });

      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === current);
        d.setAttribute('aria-selected', i === current);
      });
    }

    /* ── Autoplay ── */
    function startAutoplay() {
      if (!autoplay || reduced) return;
      clearInterval(timer);
      timer = setInterval(() => goTo(current + 1), speed);
    }

    function stopAutoplay() {
      clearInterval(timer);
    }

    /* ── Pause on hover / focus ── */
    el.addEventListener('mouseenter', stopAutoplay);
    el.addEventListener('mouseleave', startAutoplay);
    el.addEventListener('focusin',    stopAutoplay);
    el.addEventListener('focusout',   startAutoplay);

    /* ── Dots ── */
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        goTo(parseInt(dot.dataset.index, 10));
        startAutoplay();
      });
    });

    /* ── Keyboard ── */
    el.setAttribute('tabindex', '0');
    el.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { goTo(current - 1); startAutoplay(); }
      if (e.key === 'ArrowRight') { goTo(current + 1); startAutoplay(); }
    });

    /* ── Touch / Mouse drag ── */
    function onDragStart(x) {
      dragStartX = x;
      dragging   = true;
      dragDelta  = 0;
      stopAutoplay();
      // Disable CSS transition during drag for 1:1 feel
      track.style.transition = 'none';
    }

    function onDragMove(x) {
      if (!dragging) return;
      dragDelta = x - dragStartX;
      const offset = -(current * 100) + (dragDelta / el.offsetWidth) * 100;
      track.style.transform = `translateX(${offset}%)`;
    }

    function onDragEnd() {
      if (!dragging) return;
      dragging = false;
      // Re-enable transition
      track.style.transition = '';

      if (dragDelta < -SWIPE_THRESHOLD)      goTo(current + 1);
      else if (dragDelta > SWIPE_THRESHOLD)  goTo(current - 1);
      else                                   goTo(current); // snap back

      startAutoplay();
    }

    /* Touch */
    el.addEventListener('touchstart', e => onDragStart(e.touches[0].clientX), { passive: true });
    el.addEventListener('touchmove',  e => onDragMove(e.touches[0].clientX),  { passive: true });
    el.addEventListener('touchend',   onDragEnd);

    /* Mouse */
    el.addEventListener('mousedown',  e => { e.preventDefault(); onDragStart(e.clientX); });
    window.addEventListener('mousemove', e => { if (dragging) onDragMove(e.clientX); });
    window.addEventListener('mouseup',   () => { if (dragging) onDragEnd(); });

    /* ── Boot ── */
    goTo(0);
    startAutoplay();
  }

  /* ── Init all sliders on the page ── */
  function initAll() {
    document.querySelectorAll('.ylse-slider').forEach(initSlider);
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Also re-init after Shopify section renders (theme editor live preview)
  document.addEventListener('shopify:section:load', initAll);

})();
